import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { api } from '../api/client';
import { onCharge, onChargeText, ON_CHARGE_YES, ON_CHARGE_NO } from '../onCharge';

function StatusBadge({ status }) {
    const cls = `badge badge-${(status || '').toLowerCase()}`;
    return <span className={cls}>{status}</span>;
}

/**
 * Tells AP whether an on-charge request has to follow the payment. Deliberately
 * loud for Yes and quiet for No — a missed Yes costs money, a missed No does not.
 */
function OnChargeBadge({ value }) {
    const { answer, detail } = onCharge(value);
    const tone = {
        [ON_CHARGE_YES]: { bg: '#e8f5e9', fg: '#1b5e20', border: '#66bb6a' },
        [ON_CHARGE_NO]: { bg: '#f5f5f5', fg: '#666', border: '#ddd' },
    }[answer] || { bg: '#fff8e1', fg: '#8d6e00', border: '#ffca28' };

    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 13,
            fontWeight: answer === ON_CHARGE_YES ? 700 : 500,
            background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
        }}>
            {answer}{detail ? ` — ${detail}` : ''}
        </span>
    );
}

function formatDate(d) {
    if (!d) return '--';
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(d) {
    if (!d) return '--';
    return new Date(d).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatMoney(v) {
    if (!v && v !== 0) return '--';
    return '$' + Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Field({ label, value, wide }) {
    return (
        <div style={{ minWidth: wide ? 300 : 150, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 14 }}>{value || '--'}</div>
        </div>
    );
}

export default function WorkOrderPage() {
    const { pk } = useParams();
    const { accounts } = useMsal();
    const currentUserEmail = accounts[0]?.username || '';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('details');
    const [emailSending, setEmailSending] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.getWorkOrder(pk)
            .then(setData)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [pk]);

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading work order...</div>;
    if (error) return <div style={{ padding: 20, background: '#fce4ec', color: '#c62828', borderRadius: 4 }}>{error}</div>;
    if (!data) return null;

    const wo = data.workOrder;
    const vendor = data.assignments?.find(a => a.isLead)?.laborName || data.assignments?.[0]?.laborName || '';

    const buildEmailText = () => {
        const nl = '\n';
        const line = (label, val) => `${label}: ${val || '--'}${nl}`;
        let body = `WORK ORDER: ${wo.id}${nl}`;
        body += `${'='.repeat(40)}${nl}`;
        body += line('Status', wo.statusDesc);
        body += line('Type', wo.typeDesc);
        body += line('Priority', wo.priorityDesc);
        body += line('Site', `${wo.shopId} ${wo.shopName}`);
        body += line('Department', wo.departmentName);
        body += line('GL Account', wo.accountId ? `${wo.accountId} ${wo.accountName}` : null);
        body += line('On Charge', onChargeText(wo.onCharge));
        body += line('Category', wo.categoryName);
        body += line('Requester', wo.requesterName);
        body += line('Taken By', wo.takenBy);
        body += line('Vendor', vendor);
        body += line('Supervisor', wo.supervisorName);
        body += line('Auth Status', wo.authStatusDesc);
        body += line('Requested', formatDate(wo.requestedDate));
        body += line('Target Date', formatDate(wo.targetDate));
        body += line('Completed', formatDate(wo.completeDate));
        body += line('Closed', formatDate(wo.closedDate));
        body += line('Total Actual', formatMoney(wo.costTotalActual));
        body += line('Total Estimated', formatMoney(wo.costTotalEstimated));
        body += `${nl}Description:${nl}${(wo.reason || '').substring(0, 800)}${nl}`;
        if (data.statusHistory?.length > 0) {
            body += `${nl}Status History:${nl}`;
            data.statusHistory.forEach(h => { body += `  ${formatDateTime(h.date)} - ${h.description || h.status} (${h.userName || h.initials || ''})${nl}`; });
        }
        if (data.miscCosts?.length > 0) {
            body += `${nl}Costs:${nl}`;
            data.miscCosts.forEach(m => { body += `  ${m.name} - ${m.companyName || ''} - Est: ${formatMoney(m.estimatedCost)} Act: ${formatMoney(m.actualCost)} Chg: ${formatMoney(m.totalCharge)}${nl}`; });
        }
        if (data.documents?.length > 0) {
            body += `${nl}Attachments:${nl}`;
            data.documents.forEach(d => { body += `  ${d.name}${d.url ? ' - ' + d.url : ''}${nl}`; });
        }
        if (data.images?.length > 0) {
            data.images.forEach(img => { if (img.url) body += `  Photo: ${img.url}${nl}`; });
        }
        body += `${nl}View online: ${window.location.href}`;
        return body;
    };

    const handleEmail = async () => {
        const to = window.prompt('Send WO email to:', currentUserEmail);
        if (!to) return;

        setEmailSending(true);
        try {
            const text = buildEmailText();

            // Collect original MC URLs -- backend fetches them server-side for email attachments
            const attachments = [
                ...(data.documents || []).filter(d => d.originalUrl || d.url).map(d => ({ filename: d.name || 'document', url: d.originalUrl || d.url })),
                ...(data.images || []).filter(img => img.originalUrl || img.url).map(img => ({ filename: img.photo || 'photo.jpg', url: img.originalUrl || img.url })),
            ];

            const result = await api.sendEmail({
                to,
                subject: `WO ${wo.id} - ${wo.shopId} ${wo.shopName}`,
                text,
                attachments,
            });

            if (result.sent) {
                alert('Email sent to ' + to);
            }
        } catch (err) {
            alert('Email failed: ' + err.message);
        } finally {
            setEmailSending(false);
        }
    };

    const tabs = [
        { key: 'details', label: 'Details' },
        { key: 'history', label: `History (${data.statusHistory?.length || 0})` },
        { key: 'attachments', label: `Attachments (${(data.documents?.length || 0) + (data.images?.length || 0)})` },
        { key: 'costs', label: `Costs (${(data.miscCosts?.length || 0) + (data.laborActuals?.length || 0)})` },
        { key: 'tasks', label: `Tasks (${data.tasks?.length || 0})` },
        { key: 'assignments', label: `Assignments (${data.assignments?.length || 0})` },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <Link to="/" style={{ fontSize: 13, color: '#4a9eff' }}>Back to Search</Link>
            </div>

            {/* Header */}
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 22 }}>{wo.id}</h2>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <StatusBadge status={wo.status} />
                            {wo.subStatus && <StatusBadge status={wo.subStatus} />}
                            {wo.authStatus && <span className={`badge badge-${wo.authStatus.toLowerCase()}`}>{wo.authStatusDesc || wo.authStatus}</span>}
                        </div>
                    </div>
                    <div className="no-print" style={{ display: 'flex', gap: 8 }}>
                        <button onClick={handleEmail} className="btn btn-outline" disabled={emailSending}>
                            {emailSending ? 'Sending...' : 'Email'}
                        </button>
                        {((data.documents?.length > 0) || (data.images?.length > 0)) && (
                            <button onClick={async () => {
                                const files = [
                                    ...(data.documents || []).filter(d => d.originalUrl).map(d => ({ filename: d.name, url: d.originalUrl })),
                                    ...(data.images || []).filter(img => img.originalUrl).map(img => ({ filename: img.photo, url: img.originalUrl })),
                                ];
                                try {
                                    const blob = await api.downloadAll(files, `WO-${wo.id}`);
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url; a.download = `WO-${wo.id}-attachments.zip`; a.click();
                                    URL.revokeObjectURL(url);
                                } catch (err) { alert('Download failed: ' + err.message); }
                            }} className="btn btn-outline">Download</button>
                        )}
                        <Link to={`/wo/${pk}/print`} className="btn btn-outline" style={{ textDecoration: 'none' }}>Print</Link>
                    </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                    <Field label="Site ID" value={wo.shopId} />
                    <Field label="Site Name" value={wo.shopName} />
                    <Field label="Priority" value={wo.priorityDesc || wo.priority} />
                    <Field label="Department" value={wo.departmentName} />
                    <Field label="GL Account" value={wo.accountId ? `${wo.accountId} ${wo.accountName}` : null} />
                    <Field label="On Charge" value={<OnChargeBadge value={wo.onCharge} />} />
                    <Field label="Category" value={wo.categoryName} />
                    <Field label="Taken By" value={wo.takenBy} />
                    {vendor && <Field label="Vendor" value={vendor} />}
                    <Field label="Approved By" value={wo.authStatusDesc === 'Approved' ? (wo.authBy || wo.supervisorName || 'Yes') : wo.authStatusDesc} />
                    <Field label="Amount (Actual)" value={formatMoney(wo.costTotalActual)} />
                    <Field label="Amount (Estimated)" value={formatMoney(wo.costTotalEstimated)} />
                    <Field label="Requested" value={formatDate(wo.requestedDate)} />
                    <Field label="Completed" value={formatDate(wo.completeDate)} />
                    <Field label="Closed" value={formatDate(wo.closedDate)} />
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs no-print">
                {tabs.map(t => (
                    <div key={t.key} className={`tab ${activeTab === t.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(t.key)}>{t.label}</div>
                ))}
            </div>

            {/* Tab content */}
            <div className="card">
                {activeTab === 'details' && <DetailsTab wo={wo} />}
                {activeTab === 'history' && <HistoryTab history={data.statusHistory} />}
                {activeTab === 'attachments' && <AttachmentsTab documents={data.documents} images={data.images} />}
                {activeTab === 'costs' && <CostsTab miscCosts={data.miscCosts} laborActuals={data.laborActuals} wo={wo} />}
                {activeTab === 'tasks' && <TasksTab tasks={data.tasks} />}
                {activeTab === 'assignments' && <AssignmentsTab assignments={data.assignments} />}
            </div>
        </div>
    );
}

function DetailsTab({ wo }) {
    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 20 }}>
                <Field label="Type" value={wo.typeDesc} />
                <Field label="Priority" value={wo.priorityDesc} />
                <Field label="Repair Center" value={wo.repairCenter} />
                <Field label="Asset" value={wo.assetId ? `${wo.assetId} - ${wo.assetName}` : null} />
                <Field label="Requester" value={wo.requesterName} />
                <Field label="Requester Email" value={wo.requesterEmail} />
                <Field label="Supervisor" value={wo.supervisorName} />
                <Field label="Target Date" value={formatDate(wo.targetDate)} />
                <Field label="Target Hours" value={wo.targetHours} />
                <Field label="Actual Hours" value={wo.actualHours} />
                <Field label="Complete %" value={wo.completePercent != null ? `${wo.completePercent}%` : null} />
            </div>
            {wo.reason && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Description</div>
                    <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', background: '#f8f9fa', padding: 12, borderRadius: 4 }}>{wo.reason}</div>
                </div>
            )}
            {wo.instructions && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Instructions</div>
                    <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', background: '#f8f9fa', padding: 12, borderRadius: 4 }}>{wo.instructions}</div>
                </div>
            )}
            {wo.laborReport && (
                <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Labor Report</div>
                    <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', background: '#f8f9fa', padding: 12, borderRadius: 4 }}>{wo.laborReport}</div>
                </div>
            )}
        </div>
    );
}

function HistoryTab({ history }) {
    if (!history || history.length === 0) return <div style={{ color: '#999', padding: 20, textAlign: 'center' }}>No status history</div>;
    return (
        <table>
            <thead><tr><th>Date</th><th>Status</th><th>Changed By</th></tr></thead>
            <tbody>
                {history.map((h, i) => (
                    <tr key={i}>
                        <td>{formatDateTime(h.date)}</td>
                        <td><StatusBadge status={h.status} /></td>
                        <td>{h.userName || h.initials || '--'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function isImageUrl(url) {
    return /\.(jpe?g|png|gif|bmp|webp)$/i.test(url || '');
}

function isPdfUrl(url) {
    return /\.pdf$/i.test(url || '');
}

function AttachmentItem({ name, url }) {
    const [imgError, setImgError] = useState(false);
    if (!url) return <div style={{ padding: 8, color: '#999', fontSize: 12 }}>{name} -- no link available</div>;

    if (isImageUrl(url) && !imgError) {
        return (
            <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>{name}</div>
                <a href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: 500, border: '1px solid #e0e0e0', borderRadius: 4 }}
                        onError={() => setImgError(true)} />
                </a>
            </div>
        );
    }

    if (isPdfUrl(url)) {
        return (
            <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    <a href={url} target="_blank" rel="noopener noreferrer">{name}</a>
                    <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>PDF</span>
                </div>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: 8 }}>
            <a href={url} target="_blank" rel="noopener noreferrer" download style={{ fontSize: 13 }}>{name}</a>
            <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>({url.split('.').pop()?.toUpperCase() || 'FILE'})</span>
        </div>
    );
}

function AttachmentsTab({ documents, images }) {
    const allAttachments = [
        ...(documents || []).map(d => ({ name: d.name || 'Document', url: d.url })),
        ...(images || []).map(img => ({ name: img.photo || 'Photo', url: img.url })),
    ];
    if (allAttachments.length === 0) return <div style={{ color: '#999', padding: 20, textAlign: 'center' }}>No attachments</div>;

    return (
        <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>{allAttachments.length} attachment{allAttachments.length !== 1 ? 's' : ''}</div>
            {allAttachments.map((a, i) => <AttachmentItem key={i} name={a.name} url={a.url} />)}
            <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>Attachments require VPN or on-prem network access</div>
        </div>
    );
}

function CostsTab({ miscCosts, laborActuals, wo }) {
    return (
        <div>
            {/* Cost summary */}
            <div style={{ display: 'flex', gap: 24, marginBottom: 20 }}>
                <Field label="Labor Cost" value={formatMoney(wo.costLaborActual)} />
                <Field label="Parts Cost" value={formatMoney(wo.costPartActual)} />
                <Field label="Misc Cost" value={formatMoney(wo.costMiscActual)} />
                <Field label="Total Actual" value={formatMoney(wo.costTotalActual)} />
                <Field label="Total Estimated" value={formatMoney(wo.costTotalEstimated)} />
            </div>

            {laborActuals && laborActuals.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                    <h4 style={{ fontSize: 14, marginBottom: 8 }}>Labor</h4>
                    <table>
                        <thead><tr><th>Name</th><th>Type</th><th>Date</th><th>Hours</th><th style={{ textAlign: 'right' }}>Cost</th></tr></thead>
                        <tbody>
                            {laborActuals.map((l, i) => (
                                <tr key={i} style={{ cursor: 'default' }}>
                                    <td>{l.laborName}</td>
                                    <td>{l.laborType}</td>
                                    <td>{formatDate(l.workDate)}</td>
                                    <td>{l.totalHours}</td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(l.totalCost)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {miscCosts && miscCosts.length > 0 && (
                <div>
                    <h4 style={{ fontSize: 14, marginBottom: 8 }}>Miscellaneous Costs</h4>
                    <table>
                        <thead><tr><th>Item</th><th>Company</th><th>Invoice</th><th>Date</th><th>Qty</th><th style={{ textAlign: 'right' }}>Estimated</th><th style={{ textAlign: 'right' }}>Actual</th><th style={{ textAlign: 'right' }}>Charge</th></tr></thead>
                        <tbody>
                            {miscCosts.map((m, i) => (
                                <tr key={i} style={{ cursor: 'default' }}>
                                    <td>{m.name}{m.description ? ` - ${m.description}` : ''}</td>
                                    <td>{m.companyName}</td>
                                    <td>{m.invoiceNumber}</td>
                                    <td>{formatDate(m.date)}</td>
                                    <td>{m.quantityActual || ''}</td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(m.estimatedCost)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(m.actualCost)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(m.totalCharge)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {(!laborActuals || laborActuals.length === 0) && (!miscCosts || miscCosts.length === 0) && (
                <div style={{ color: '#999', padding: 20, textAlign: 'center' }}>No cost records</div>
            )}
        </div>
    );
}

function TasksTab({ tasks }) {
    if (!tasks || tasks.length === 0) return <div style={{ color: '#999', padding: 20, textAlign: 'center' }}>No tasks</div>;
    return (
        <table>
            <thead><tr><th>#</th><th>Task</th><th>Status</th><th>Hours (Est/Act)</th><th>Comments</th></tr></thead>
            <tbody>
                {tasks.map((t, i) => (
                    <tr key={i} style={{ cursor: 'default' }}>
                        <td>{t.taskNo}</td>
                        <td style={{ maxWidth: 400 }}>{t.action}</td>
                        <td>
                            {t.complete ? <span style={{ color: '#2e7d32' }}>Complete</span>
                                : t.fail ? <span style={{ color: '#c62828' }}>Failed</span>
                                : <span style={{ color: '#f57f17' }}>Pending</span>}
                        </td>
                        <td>{t.hoursEstimated || '--'} / {t.hoursActual || '--'}</td>
                        <td style={{ maxWidth: 300, fontSize: 12 }}>{t.comments}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function AssignmentsTab({ assignments }) {
    if (!assignments || assignments.length === 0) return <div style={{ color: '#999', padding: 20, textAlign: 'center' }}>No assignments</div>;
    return (
        <table>
            <thead><tr><th>Name</th><th>Lead</th><th>Hours</th><th>Assigned</th><th>Completed</th></tr></thead>
            <tbody>
                {assignments.map((a, i) => (
                    <tr key={i} style={{ cursor: 'default' }}>
                        <td>{a.laborName}</td>
                        <td>{a.isLead ? 'Yes' : ''}</td>
                        <td>{a.assignedHours}</td>
                        <td>{formatDate(a.assignedDate)}</td>
                        <td>{formatDate(a.completedDate)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
